import { payResources } from "../resources/resource-pool";
import { getArmyDeploymentPreview } from "../selectors/army-deployment";
import { getArmyDefinitionV3ForInstance, getCardInstance } from "../selectors/card-instances";
import { getGarrisonSnapshot } from "../selectors/garrison";
import type { DeployArmyAction, GameEvent, GameStateV3, GameStateV4, GameStateV5 } from "../types";
import type { TransitionResult } from "./advance-phase";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

export function applyDeployArmy(
  state: Readonly<GameStateV3 | GameStateV4 | GameStateV5>,
  action: DeployArmyAction,
  actionSequence: number,
): TransitionResult {
  const instance = getCardInstance(state, action.armyCardInstanceId);
  const definition = getArmyDefinitionV3ForInstance(state, action.armyCardInstanceId);
  const preview = getArmyDeploymentPreview(state, action);
  if (instance === undefined || definition === undefined || !preview.legal) {
    throw new Error("Validated Army deployment lost its schema-3 card definition or preview.");
  }

  const player = state.players[action.playerId];
  const lane = state.lanes[action.laneId];
  const side = lane.players[action.playerId];
  const garrisonBefore = getGarrisonSnapshot(state, action.playerId, action.laneId);
  const previousKingdomPool = { ...player.kingdomPool };
  const costPaid = { ...definition.cost };
  const printedUpkeep = { ...definition.upkeep };
  const remainingKingdomPool = payResources(previousKingdomPool, costPaid);
  const nextState: GameStateV3 | GameStateV4 | GameStateV5 = {
    ...state,
    players: {
      ...state.players,
      [action.playerId]: {
        ...player,
        hand: player.hand.filter((cardInstanceId) => cardInstanceId !== action.armyCardInstanceId),
        kingdomPool: remainingKingdomPool,
        actionsUsed: {
          ...player.actionsUsed,
          deploymentActions: 1,
        },
      },
    },
    lanes: {
      ...state.lanes,
      [action.laneId]: {
        ...lane,
        players: {
          ...lane.players,
          [action.playerId]: {
            ...side,
            armies: [
              ...side.armies,
              {
                cardInstanceId: action.armyCardInstanceId,
                armySize: definition.armySize,
                attack: definition.attack,
                guard: definition.guard,
              },
            ],
          },
        },
      },
    },
  };
  const events: GameEvent[] = [
    {
      type: "kingdom-pool-spent",
      eventId: eventId(actionSequence, 0),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      armyCardInstanceId: action.armyCardInstanceId,
      cardDefinitionId: instance.cardDefinitionId,
      reason: "deploy-army",
      costPaid,
      previousKingdomPool,
      remainingKingdomPool,
    },
    {
      type: "army-deployed",
      eventId: eventId(actionSequence, 1),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      armyCardInstanceId: action.armyCardInstanceId,
      cardDefinitionId: instance.cardDefinitionId,
      armySize: definition.armySize,
      printedAttack: definition.attack,
      printedGuard: definition.guard,
      printedUpkeep,
      costPaid,
      remainingKingdomPool,
      armyCountBefore: garrisonBefore.armyCount,
      armyCountAfter: preview.armyCountAfter,
      totalArmySizeBefore: garrisonBefore.totalArmySize,
      totalArmySizeAfter: preview.totalArmySizeAfter,
      ...(preview.commanderCapacity === undefined
        ? {}
        : { commanderCapacity: preview.commanderCapacity }),
      projectedNextSupplySustainable: preview.projectedSustainable,
    },
    {
      type: "deployment-action-used",
      eventId: eventId(actionSequence, 2),
      actionSequence,
      playerId: action.playerId,
      actionType: "deploy-army",
      deploymentActionsUsed: 1,
    },
  ];

  return { state: nextState, events };
}
